import { cn } from '../../lib/cn'

export interface ScorecardColumn {
  key: string
  label: string
  /** Right-aligns the column — the default for every numeric column. */
  numeric?: boolean
}

export interface ScorecardRow {
  id: string
  /** Tints the row and marks it as the reading member's own. */
  isYou?: boolean
  cells: Record<string, React.ReactNode>
}

interface ScorecardProps {
  caption: string
  columns: ScorecardColumn[]
  rows: ScorecardRow[]
  className?: string
}

/**
 * The standings table (components card §VIII) — tabular Fraunces numerals,
 * brass rules, and the reading member's own row tinted sage.
 *
 * The tint alone can't carry "this row is yours" for anyone who can't see it,
 * so the member's row also gets a visually-hidden marker in its first cell.
 * The caption is rendered rather than hidden — these tables are dense and the
 * heading above them is often several sections up.
 */
export function Scorecard({ caption, columns, rows, className }: ScorecardProps) {
  return (
    <table className={cn('w-full border-collapse tabular-nums', className)}>
      <caption className="mb-4 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
        {caption}
      </caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={cn(
                'border-b border-brass px-3.5 pb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep',
                column.numeric ? 'text-right' : 'text-left',
              )}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className={cn(row.isYou && 'bg-sage-soft/40')}>
            {columns.map((column, index) => (
              <td
                key={column.key}
                className={cn(
                  'border-b border-brass/25 px-3.5 py-3.5 text-sm text-ink',
                  column.numeric && 'text-right font-display tabular-nums text-pine',
                )}
              >
                {index === 0 && row.isYou && <span className="sr-only">Your standing: </span>}
                {row.cells[column.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
